import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useCallback, useRef } from 'react';
import {
  fetchWeldParameters,
  setWeldParametersModalOpen,
  clearWeldParameters
} from '../actions';

/**
 * @typedef {Object} WeldParametersData
 * @property {Object} groupedByMachine
 * @property {Array} [groupedByMachine.p625]
 * @property {Array} [groupedByMachine.m500]
 * @property {Array} [groupedByMachine.p600z]
 */

/**
 * Custom hook for managing weld parameters
 * @param {number} projectId - The project ID to fetch weld parameters for
 * @returns {{
 *   weldParameters: WeldParametersData,
 *   isLoading: boolean,
 *   error: string | null,
 *   isModalOpen: boolean,
 *   machineType: string | null,
 *   openModal: (machineType?: string) => void,
 *   closeModal: () => void,
 *   clearData: () => void,
 *   refresh: () => void
 * }} Weld parameters state and actions
 */
export const useWeldParameters = (projectId) => {
  const dispatch = useDispatch();

  const {
    weldParameters = {},
    weldParametersLoading = false,
    weldParametersError = null,
    weldParametersModalOpen = false,
    weldParametersMachineType = null
  } = useSelector((state) => state.productionData || {});

  // Track previous projectId to detect changes
  const prevProjectIdRef = useRef(undefined);

  // Clear data when projectId changes (even if component is still mounted)
  useEffect(() => {
    if (prevProjectIdRef.current !== undefined && prevProjectIdRef.current !== projectId) {
      dispatch(clearWeldParameters());
    }
    prevProjectIdRef.current = projectId;
  }, [dispatch, projectId]);

  // Don't auto-fetch on mount - only fetch when explicitly called via refresh()
  // useEffect(() => {
  //   if (projectId) {
  //     dispatch(fetchWeldParameters(projectId));
  //   }
  // }, [dispatch, projectId]);

  // Open modal with machine type (p625 or m500)
  const openWeldParametersModal = useCallback((machineType) => {
    dispatch(setWeldParametersModalOpen(true, machineType));
  }, [dispatch]);

  // Close modal
  const closeWeldParametersModal = useCallback(() => {
    dispatch(setWeldParametersModalOpen(false, null));
  }, [dispatch]);

  // Clear weld parameters data
  const clearWeldParametersData = useCallback(() => {
    dispatch(clearWeldParameters());
  }, [dispatch]);

  // Refresh weld parameters
  const refreshWeldParameters = useCallback(() => {
    if (projectId) {
      dispatch(fetchWeldParameters(projectId));
    }
  }, [dispatch, projectId]);

  /** @type {{
   *   weldParameters: WeldParametersData,
   *   isLoading: boolean,
   *   error: string | null,
   *   isModalOpen: boolean,
   *   machineType: string | null,
   *   openModal: (machineType?: string) => void,
   *   closeModal: () => void,
   *   clearData: () => void,
   *   refresh: () => void
   * }} */
  const result = {
    weldParameters,
    isLoading: weldParametersLoading,
    error: weldParametersError,
    isModalOpen: weldParametersModalOpen,
    machineType: weldParametersMachineType,
    openModal: openWeldParametersModal,
    closeModal: closeWeldParametersModal,
    clearData: clearWeldParametersData,
    refresh: refreshWeldParameters
  };
  
  return result;
};

export default useWeldParameters;

